import { CalendarDays, Clock } from "lucide-react";
import { Popover } from "radix-ui";
import * as React from "react";
import { cn } from "../lib/cn.js";
import { formatDateText, parseDateText, parseTimeText } from "../lib/date-text.js";
import { buttonVariants } from "./button.js";
import { fieldClasses } from "./input.js";

type FieldProps = Omit<
  React.ComponentProps<"input">,
  "type" | "value" | "defaultValue" | "onChange" | "inputMode"
> & {
  /** ISO value (`YYYY-MM-DD` / `HH:MM`), "" for none. */
  value: string;
  /** Called with the ISO value, or "" when the field is empty or its text is invalid. */
  onValueChange: (value: string) => void;
};

/**
 * Text that follows `value` and reports parsed values: complete forms while typing, looser
 * ones on blur (then normalised). Invalid text reports "" and is marked after blur.
 */
function useTypedValue(
  value: string,
  onValueChange: (value: string) => void,
  format: (value: string) => string,
  parse: (text: string, strict?: boolean) => string | null,
) {
  const [text, setText] = React.useState(() => format(value));
  const [invalid, setInvalid] = React.useState(false);
  // The value `text` stands for; a different `value` from outside replaces the text.
  const [shown, setShown] = React.useState(value);
  if (value !== shown) {
    setShown(value);
    setText(format(value));
    setInvalid(false);
  }
  const report = (next: string) => {
    setShown(next);
    if (next !== value) onValueChange(next);
  };
  return {
    text,
    invalid,
    /** A value picked from a list: shown and reported at once. */
    select: (next: string) => {
      setText(format(next));
      setInvalid(false);
      report(next);
    },
    onChange: (event: React.ChangeEvent<HTMLInputElement>) => {
      const next = event.target.value;
      setText(next);
      setInvalid(false);
      if (next.trim() === "") report("");
      else {
        const parsed = parse(next, true);
        if (parsed) report(parsed);
      }
    },
    onBlur: () => {
      if (text.trim() === "") return report("");
      const parsed = parse(text);
      if (parsed) {
        setText(format(parsed));
        report(parsed);
      } else {
        setInvalid(true);
        report("");
      }
    },
  };
}

/** The icon button inside the field's right edge. */
const iconButton = cn(
  buttonVariants({ variant: "ghost", size: "icon" }),
  "absolute top-1 right-1 data-[state=open]:bg-muted data-[state=open]:text-foreground",
);

/**
 * A date typed as `DD.MM.YYYY`. The calendar button opens the browser's picker on a hidden
 * native date input, whose own display format is never shown.
 */
export function DateInput({
  value,
  onValueChange,
  className,
  disabled,
  onBlur,
  ...props
}: FieldProps) {
  const field = useTypedValue(value, onValueChange, formatDateText, parseDateText);
  const picker = React.useRef<HTMLInputElement>(null);
  return (
    <div className={cn("relative", className)}>
      {/* Under the button, so the picker opens next to it. */}
      <input
        ref={picker}
        type="date"
        tabIndex={-1}
        aria-hidden
        disabled={disabled}
        value={value}
        onChange={(event) => field.select(event.target.value)}
        className="pointer-events-none absolute top-1 right-1 size-7 opacity-0"
      />
      <input
        {...props}
        type="text"
        inputMode="decimal"
        autoComplete="off"
        placeholder="DD.MM.YYYY"
        disabled={disabled}
        value={field.text}
        aria-invalid={field.invalid || props["aria-invalid"]}
        onChange={field.onChange}
        onBlur={(event) => {
          field.onBlur();
          onBlur?.(event);
        }}
        className={cn(fieldClasses, "relative h-9 pr-9 tabular-nums")}
      />
      <button
        type="button"
        aria-label="Pick a date"
        title="Pick a date"
        disabled={disabled}
        className={iconButton}
        onClick={() => {
          const input = picker.current;
          if (!input) return;
          try {
            input.showPicker();
          } catch {
            input.focus();
            input.click();
          }
        }}
      >
        <CalendarDays aria-hidden />
      </button>
    </div>
  );
}

const STEP = 15;
const TIMES = Array.from({ length: (24 * 60) / STEP }, (_, i) => {
  const minutes = i * STEP;
  return `${String(Math.floor(minutes / 60)).padStart(2, "0")}:${String(minutes % 60).padStart(2, "0")}`;
});

/**
 * A 24 h time typed as `HH:MM`, or picked from a list in 15 min steps. The list is not
 * portalled, so it stays inside a modal `<dialog>` (top layer).
 */
export function TimeInput({
  value,
  onValueChange,
  className,
  disabled,
  onBlur,
  ...props
}: FieldProps) {
  const field = useTypedValue(value, onValueChange, (v) => v, parseTimeText);
  const [open, setOpen] = React.useState(false);
  const list = React.useRef<HTMLDivElement>(null);
  // The chosen time, or the closest step before it, is focused when the list opens.
  const current = TIMES.findLast((time) => time <= (value || "09:00")) ?? TIMES[0];
  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <div className={cn("relative", className)}>
        <input
          {...props}
          type="text"
          inputMode="decimal"
          autoComplete="off"
          placeholder="HH:MM"
          disabled={disabled}
          value={field.text}
          aria-invalid={field.invalid || props["aria-invalid"]}
          onChange={field.onChange}
          onBlur={(event) => {
            field.onBlur();
            onBlur?.(event);
          }}
          className={cn(fieldClasses, "h-9 pr-9 tabular-nums")}
        />
        <Popover.Trigger
          type="button"
          aria-label="Pick a time"
          title="Pick a time"
          disabled={disabled}
          className={iconButton}
        >
          <Clock aria-hidden />
        </Popover.Trigger>
      </div>
      <Popover.Content
        align="end"
        sideOffset={4}
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          const button = list.current?.querySelector<HTMLButtonElement>(`[data-time="${current}"]`);
          if (!button || !list.current) return;
          // Scroll only the list, never the dialog behind it.
          list.current.scrollTop = button.offsetTop - list.current.clientHeight / 2;
          button.focus({ preventScroll: true });
        }}
        className="z-50 border border-input bg-popover text-popover-foreground shadow-window data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:duration-100"
      >
        <div
          ref={list}
          role="listbox"
          aria-label="Times"
          className="relative flex max-h-64 w-24 flex-col overflow-y-auto p-1"
        >
          {TIMES.map((time) => (
            <button
              key={time}
              type="button"
              role="option"
              aria-selected={time === value}
              data-time={time}
              onClick={() => {
                field.select(time);
                setOpen(false);
              }}
              className="flex h-8 pointer-coarse:h-11 shrink-0 items-center px-3 text-sm text-secondary-foreground tabular-nums outline-none select-none hover:bg-muted hover:text-foreground focus-visible:bg-muted focus-visible:text-foreground aria-selected:text-brand"
            >
              {time}
            </button>
          ))}
        </div>
      </Popover.Content>
    </Popover.Root>
  );
}
